import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", function () {
  // Add scroll-lock to body to prevent scrolling until animations are ready
  document.body.classList.add('scroll-lock');

  // Remove fonts-loading when fonts are ready, or after a fallback timeout (Safari fix)
  function removeFontsLoadingAndRefresh() {
    document.body.classList.remove('fonts-loading');
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      window.ScrollTrigger.refresh();
    }
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(removeFontsLoadingAndRefresh);
    // Fallback: always remove after 2.5s in case Safari or a font fails
    setTimeout(removeFontsLoadingAndRefresh, 2500);
  } else {
    // If Font Loading API not supported, fallback immediately
    removeFontsLoadingAndRefresh();
  }

  // --- Section Header Logic ---
  function initSectionHeader() {
    const sectionHeader = document.querySelector('.section-header');
    if (!sectionHeader) return;

    const header = sectionHeader.querySelector('h1');
    if (!header) return;

    // Helper to split header into spans per character
    function splitHeaderIntoSpans() {
      if (!header.querySelector('.char-container')) {
        const chars = header.textContent.split('');
        header.innerHTML = '';
        chars.forEach(char => {
          // Container for both main and clone
          const container = document.createElement('span');
          container.className = 'char-container';
          container.style.display = 'inline-block';
          container.style.position = 'relative';
          container.style.verticalAlign = 'baseline';

          // Main character
          const mainChar = document.createElement('span');
          mainChar.textContent = char === ' ' ? '\u00A0' : char;
          mainChar.className = 'char-main';
          mainChar.style.display = 'inline-block';
          mainChar.style.position = 'relative';
          mainChar.style.zIndex = '2';

          // Duplicate above
          const cloneAbove = document.createElement('span');
          cloneAbove.textContent = char === ' ' ? '\u00A0' : char;
          cloneAbove.className = 'char-clone-above';
          cloneAbove.style.position = 'absolute';
          cloneAbove.style.left = '0';
          cloneAbove.style.right = '0';
          cloneAbove.style.top = '-100%';
          cloneAbove.style.zIndex = '1';
          cloneAbove.style.display = 'inline-block';
          cloneAbove.style.width = '100%';
          cloneAbove.style.textAlign = 'inherit';
          cloneAbove.style.pointerEvents = 'none';

          // Duplicate below
          const cloneBelow = document.createElement('span');
          cloneBelow.textContent = char === ' ' ? '\u00A0' : char;
          cloneBelow.className = 'char-clone-below';
          cloneBelow.style.position = 'absolute';
          cloneBelow.style.left = '0';
          cloneBelow.style.right = '0';
          cloneBelow.style.bottom = '-100%';
          cloneBelow.style.zIndex = '1';
          cloneBelow.style.display = 'inline-block';
          cloneBelow.style.width = '100%';
          cloneBelow.style.textAlign = 'inherit';
          cloneBelow.style.pointerEvents = 'none';

          container.appendChild(mainChar);
          container.appendChild(cloneAbove);
          container.appendChild(cloneBelow);
          header.appendChild(container);
        });
      }
    }

    // Font size fit logic (measure plain text, not split spans)
    let multipliers = [];

    function fitHeaderFont() {
      splitHeaderIntoSpans();
      // Use a plain span for measurement (not split into containers)
      const measureSpan = document.createElement('span');
      measureSpan.style.visibility = 'hidden';
      measureSpan.style.position = 'absolute';
      measureSpan.style.whiteSpace = 'nowrap';
      measureSpan.style.left = '-9999px';
      measureSpan.style.top = '0';
      measureSpan.style.fontFamily = getComputedStyle(header).fontFamily;
      measureSpan.style.fontWeight = getComputedStyle(header).fontWeight;
      measureSpan.style.letterSpacing = getComputedStyle(header).letterSpacing;
      measureSpan.style.textTransform = getComputedStyle(header).textTransform;
      measureSpan.style.lineHeight = getComputedStyle(header).lineHeight;
      measureSpan.style.fontVariant = getComputedStyle(header).fontVariant;
      measureSpan.style.fontStyle = getComputedStyle(header).fontStyle;
      measureSpan.style.textAlign = 'center';
      // Use the header's text content (not split)
      measureSpan.textContent = Array.from(header.querySelectorAll('.char-main')).map(span => span.textContent).join('');
      document.body.appendChild(measureSpan);

      const charSpans = Array.from(header.querySelectorAll('.char-container'));
      if (multipliers.length !== charSpans.length) {
        multipliers = charSpans.map(() => 100 + Math.random() * 120);
      }
      const vw = window.innerWidth;
      let min = 10, max = 1000, best = min;
      while (min <= max) {
        let mid = Math.floor((min + max) / 2);
        measureSpan.style.fontSize = mid + "px";
        const width = measureSpan.offsetWidth;
        if (width > vw * 0.98) {
          max = mid - 1;
        } else {
          best = mid;
          min = mid + 1;
        }
      }
      header.style.fontSize = best + "px";
      document.body.removeChild(measureSpan);
    }

    function animateCharsOnScroll() {
      splitHeaderIntoSpans();
      const charSpans = Array.from(header.querySelectorAll('.char-container'));
      const scrollY = window.scrollY || window.pageYOffset;
      charSpans.forEach((span, i) => {
        // Alternate direction: even index down, odd index up
        const direction = i % 2 === 0 ? 1 : -1;
        // Linear translation based on scroll position (increase amplitude)
        const translateY = scrollY * 0.75 * direction * (multipliers[i] / 200);
        span.style.transform = `translateY(${translateY}px)`;
      });
    }

    // Make the function available globally for Lenis
    window.animateSectionHeaderChars = animateCharsOnScroll;

    // Initial fit and animate
    fitHeaderFont();
    animateCharsOnScroll();

    // On window resize, recalculate the header font size and re-animate
    window.addEventListener('resize', () => {
      // If fonts are still loading, wait for them before recalculating
      if (document.body.classList.contains('fonts-loading')) {
        const observer = new MutationObserver(() => {
          if (!document.body.classList.contains('fonts-loading')) {
            observer.disconnect();
            fitHeaderFont();
            animateCharsOnScroll();
          }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      } else {
        fitHeaderFont();
        animateCharsOnScroll();
      }
    });

    window.addEventListener('scroll', animateCharsOnScroll, { passive: true });

    // If header text might change dynamically, observe and refit
    const observer = new MutationObserver(() => {
      fitHeaderFont();
      animateCharsOnScroll();
    });
    observer.observe(header, { childList: true, characterData: true, subtree: true });
  }

  // Wait for .fonts-loading to be removed from <body>
  function waitForFontsLoadingRemoval(callback) {
    if (!document.body.classList.contains('fonts-loading')) {
      callback();
      return;
    }
    const observer = new MutationObserver(() => {
      if (!document.body.classList.contains('fonts-loading')) {
        observer.disconnect();
        callback();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  waitForFontsLoadingRemoval(initSectionHeader);

  // --- GSAP/ScrollTrigger Animation Logic ---
  function onFontsReady() {
    document.body.classList.remove('fonts-loading');

    // --- TITLE ANIMATION ---
    const titleEls = Array.from(document.querySelectorAll('.scrolling-video-cards-title'));
    const titleStates = new WeakMap();

    titleEls.forEach(title => {
      if (titleStates.get(title)?.initialized || title.querySelector('.char')) return;
      const initTitleAnimation = () => {
        if (titleStates.get(title)?.initialized) return;
        titleStates.set(title, { initialized: true });
        const processNode = node => {
          if (node.nodeType === Node.TEXT_NODE) {
            const parts = node.textContent.split(/(\s+)/);
            const frag = document.createDocumentFragment();
            parts.forEach(part => {
              if (!part) return;
              if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part)); else {
                const w = document.createElement('span'); w.className='word';
                for (const ch of part) { const c=document.createElement('span'); c.className='char'; c.textContent=ch; c.style.display='inline-block'; w.appendChild(c);} frag.appendChild(w);
              }
            });
            return frag;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const clone = node.cloneNode(false); node.childNodes.forEach(ch=>clone.appendChild(processNode(ch))); return clone;
          }
          return document.createDocumentFragment();
        };
        const originalChildren = Array.from(title.childNodes);
        title.innerHTML=''; originalChildren.forEach(n=>title.appendChild(processNode(n)));
        const chars = Array.from(title.querySelectorAll('.char'));
        gsap.set(chars, { opacity:0, y:30, filter:'blur(8px)', force3D:true });
        title.classList.add('title-initialized');
        setTimeout(()=>{
          const tlIn = gsap.timeline({ onComplete: ()=>{ const st=titleStates.get(title)||{}; st.fadeReady=true; titleStates.set(title, st); } });
          tlIn.to(chars, { opacity:1, y:0, filter:'blur(0px)', duration:0.65, ease:'power2.out', stagger:{ each:0.02, from:'start' } });
          const fadeTL = gsap.timeline({ paused:true }).to(chars, { opacity:0, y:30, filter:'blur(8px)', stagger:0.012, duration:0.35, ease:'power2.in' },0);
          const st = titleStates.get(title)||{}; st.fadeTL = fadeTL; st.chars = chars; titleStates.set(title, st);
          ScrollTrigger.refresh();
        },30);
      };
      if (window.__loaderHiddenFired) initTitleAnimation(); else window.addEventListener('app:loaderHidden', ()=>{ window.__loaderHiddenFired=true; initTitleAnimation(); }, { once:true });
    });

    // Main containers and collections
    const wrapper = document.querySelector('.scrolling-video-cards');
    const videos = gsap.utils.toArray('.trailer-videobox video');
    const boxVideos = gsap.utils.toArray('.trailer-box-video');
    const boxes = gsap.utils.toArray('.trailer-box');
    const numBoxes = boxes.length;
    const spacer = document.querySelector('.scrolling-video-cards-spacer');
    const trailerBoxes = document.querySelector('.scrolling-video-cards-boxes');

    // --- SYNC FOREGROUND/BACKGROUND VIDEO PLAYBACK ---
    // Ensure each .trailer-box-video stays in tight sync with its corresponding background .trailer-videobox video.
    (function syncPairedVideos(){
      if (!videos.length || !boxVideos.length) return;
      const count = Math.min(videos.length, boxVideos.length);
      const DESYNC_THRESHOLD = 0.05; // seconds difference before correction (approx 3 frames at 60fps)
      const LONG_DESYNC_THRESHOLD = 0.35; // force reset if very far apart (e.g., one looped)
      for (let i=0; i<count; i++) {
        const master = videos[i]; // background video acts as master clock
        const slave  = boxVideos[i];
        if (!master || !slave) continue;

        // Try to align playback rate & state
        const alignState = () => {
          if (!master.paused && slave.paused) slave.play().catch(()=>{});
          if (master.paused && !slave.paused) slave.pause();
          if (slave.playbackRate !== master.playbackRate) slave.playbackRate = master.playbackRate;
        };

        const resync = () => {
          if (!master.duration || !slave.duration) return; // metadata not ready yet
          // Fast check difference
            let diff = master.currentTime - slave.currentTime;
          // If one looped back sooner than the other, detect wrap-around scenario
          if (Math.abs(diff) > LONG_DESYNC_THRESHOLD) {
            // Hard reset if grossly out of sync
            try { slave.currentTime = master.currentTime; } catch(e) {}
            return;
          }
          if (Math.abs(diff) > DESYNC_THRESHOLD) {
            // Soft correction: jump slave to master currentTime
            try { slave.currentTime = master.currentTime; } catch(e) {}
          }
        };

        // Initial alignment once metadata is loaded
        const onMeta = () => {
          try { slave.currentTime = master.currentTime; } catch(e) {}
          alignState();
        };
        if (master.readyState >= 1) onMeta(); else master.addEventListener('loadedmetadata', onMeta, { once:true });
        if (slave.readyState < 1) slave.addEventListener('loadedmetadata', ()=>{ try { slave.currentTime = master.currentTime; } catch(e) {} }, { once:true });

        master.addEventListener('play', alignState);
        master.addEventListener('pause', alignState);
        // Use requestVideoFrameCallback when available for tight sync
        if (master.requestVideoFrameCallback) {
          const frameLoop = () => {
            resync();
            master.requestVideoFrameCallback(frameLoop);
          };
          master.requestVideoFrameCallback(frameLoop);
        } else {
          // Fallback: periodic check
          setInterval(resync, 120);
        }
      }
    })();

  // --- SYNC VIDEO PLAYBACK ---
  if (trailerBoxes && boxes.length) {
    const widthDiff = trailerBoxes.scrollWidth - window.innerWidth;
    if (widthDiff <= 0) {
      // No horizontal overflow: show all, enable nav scrolling, skip pin.
      videos.forEach((vid,i)=>{
        const c = vid.closest('.trailer-videobox');
        if (c) c.style.opacity = i===0? '1':'0';
      });
      boxVideos.forEach(fg => { fg.style.opacity = '1'; });
      document.body.classList.add('nav-scroll-enabled');
    } else {
      // Using progress-based crossfade with delayed start inside each segment
      const CROSSFADE_WINDOW = 0.18; // portion of a segment used for actual crossfade
      const CROSSFADE_DELAY  = 0.35; // fraction into segment before fade starts (0..1)
      const horizontalST = gsap.to(trailerBoxes, {
        x: () => `-${trailerBoxes.scrollWidth - window.innerWidth}px`,
        ease: 'none',
        scrollTrigger: {
          trigger: wrapper,
          start: 'top top',
          end: () => `+=${trailerBoxes.scrollWidth - window.innerWidth}`,
          scrub: 1,
          pin: true,
          invalidateOnRefresh: true,
          markers: false,
          onUpdate: self => {
            // While pinned, ensure nav-scroll-enabled is FALSE
            if (document.body.classList.contains('nav-scroll-enabled')) {
              document.body.classList.remove('nav-scroll-enabled');
            }
            // --- VIDEO CROSSFADE (original progress-based) ---
            if (videos.length && boxVideos.length) {
              const rawIndex = self.progress * (videos.length - 1);
              const baseIndex = Math.floor(rawIndex);
              const blend = rawIndex - baseIndex; // 0..1 within current segment
              const nextIndex = baseIndex + 1;

              videos.forEach((bgVid, i) => {
                const container = bgVid.closest('.trailer-videobox');
                if (!container) return;
                let targetOpacity = 0;
                if (i === baseIndex) {
                  if (nextIndex >= videos.length) {
                    targetOpacity = 1; // last segment
                  } else if (blend <= CROSSFADE_DELAY) {
                    targetOpacity = 1; // hold fully visible until delay point
                  } else if (blend <= CROSSFADE_DELAY + CROSSFADE_WINDOW) {
                    const local = (blend - CROSSFADE_DELAY) / CROSSFADE_WINDOW; // 0..1 in crossfade
                    const eased = Math.pow(local, 0.6);
                    targetOpacity = 1 - eased; // fade out during window
                  } else {
                    targetOpacity = 0; // after crossfade completes
                  }
                } else if (i === nextIndex) {
                  if (blend <= CROSSFADE_DELAY) {
                    targetOpacity = 0; // not yet
                  } else if (blend <= CROSSFADE_DELAY + CROSSFADE_WINDOW) {
                    const local = (blend - CROSSFADE_DELAY) / CROSSFADE_WINDOW;
                    const eased = Math.pow(local, 0.6);
                    targetOpacity = eased; // fade in
                  } else {
                    targetOpacity = 1; // fully visible after window
                  }
                } else if (i < baseIndex) {
                  targetOpacity = 0;
                } else if (i > nextIndex) {
                  targetOpacity = 0;
                }
                container.style.opacity = targetOpacity.toFixed(3);
              });
              boxVideos.forEach(fg => { fg.style.opacity = '1'; });
            }
            // Drive fade of titles based on early progress (unchanged)
            titleEls.forEach(title => {
              const st = titleStates.get(title);
              if (!st || !st.fadeTL || !st.fadeReady) return;
              const mapped = gsap.utils.clamp(0,1, self.progress / 0.08);
              if (mapped === 0) {
                st.fadeTL.pause(0);
                gsap.set(st.chars, { opacity:1, y:0, filter:'blur(0px)' });
              } else {
                st.fadeTL.progress(mapped);
              }
            });
          },
          onLeave: () => {
            // Mark unpinned; wait until user scrolls 10px further before enabling
            window.__navUnpinnedAt = window.scrollY;
            window.__navPendingEnable = true;
            if (!window.__navScrollPostUnpinListener) {
              window.__navScrollPostUnpinListener = () => {
                if (window.__navPendingEnable && (window.scrollY - window.__navUnpinnedAt) >= 50) {
                  document.body.classList.add('nav-scroll-enabled');
                  window.__navPendingEnable = false;
                }
              };
              window.addEventListener('scroll', window.__navScrollPostUnpinListener, { passive: true });
            }
          },
          onLeaveBack: () => {
            // Re-entering pin region from below (scrolling back up): disable again
            document.body.classList.remove('nav-scroll-enabled');
            window.__navPendingEnable = false;
          }
        }
      });
      window.__horizontalScrollTrigger = horizontalST;
      document.body.classList.remove('nav-scroll-enabled');
      videos.forEach((vid, i) => {
        const container = vid.closest('.trailer-videobox');
        if (container) container.style.opacity = i === 0 ? '1' : '0';
      });
      boxVideos.forEach(fg => { fg.style.opacity = '1'; });
    }
  }

    // Set initial opacity for videos (only the first is visible at start)
    videos.forEach((vid, i) => {
      gsap.set(vid.closest('.trailer-videobox'), { opacity: i === 0 ? 1 : 0 });
    });

    // --- REPEATED WORD ANIMATION ---
    const h2 = document.querySelector('.repeated-word h2');
    if (h2) {
      gsap.to(h2, {
        xPercent: -80,
        ease: "none", // Use "none" for smooth scrubbing
        scrollTrigger: {
          trigger: h2,
          start: "top bottom", // when h2 enters the viewport
          end: "bottom top",      // when h2 reaches the top of the viewport
          scrub: true,         // <-- enables scrubbing
          // markers: true,
          anticipatePin: 1
        }
      });
    }

    // --- VIDEO ACCORDION ---
    const accordion = document.querySelector('.video-accordion');
    const blocks = document.querySelectorAll('.video-accordion .video-block');

    // Create modal/lightbox elements once
    let videoModal = document.getElementById('video-modal');
    if (!videoModal) {
      videoModal = document.createElement('div');
      videoModal.id = 'video-modal';
      videoModal.innerHTML = `
        <button id="close-video-modal">&times;</button>
        <div class="video-modal-content">
          <video id="modal-video" controls></video>
        </div>
      `;
      document.body.appendChild(videoModal);

      // Close modal on button click or background click
      videoModal.querySelector('#close-video-modal').onclick = () => {
        videoModal.style.opacity = 0;
        videoModal.style.pointerEvents = 'none';
        const modalVideo = videoModal.querySelector('#modal-video');
        modalVideo.pause();
        modalVideo.src = '';
      };
      videoModal.addEventListener('click', e => {
        if (e.target === videoModal) {
          videoModal.querySelector('#close-video-modal').click();
        }
      });
    }

    if (accordion && blocks.length > 0) {
      // Dynamic scale for short viewport heights (desktop/laptop) to avoid cropping
      function applyAccordionScale() {
        const minDesktopWidth = 768;
        if (window.innerWidth < minDesktopWidth) {
          accordion.classList.remove('short-vh');
          accordion.style.removeProperty('--va-scale');
          return;
        }
        const vh = window.innerHeight;
        // Only engage scaling below threshold
        if (vh > 860) {
          accordion.classList.remove('short-vh');
          accordion.style.removeProperty('--va-scale');
          return;
        }
        accordion.classList.add('short-vh');
        // Measure active block desired height (fallback to first block target)
        const active = accordion.querySelector('.video-block.active') || blocks[0];
        let desired = 520; // desktop active height baseline from CSS
        if (active) {
          // Determine expected height from CSS vars / computed
          const comp = getComputedStyle(active);
          const h = parseFloat(comp.height);
          if (!isNaN(h) && h > 0) desired = h;
        }
        // Available vertical space inside section (minus a small margin/padding estimate)
        const available = vh - 40; // margin buffer
        let scale = available / desired;
        if (scale > 1) scale = 1; // never upscale
        if (scale < 0.65) scale = 0.65; // readability floor
        accordion.style.setProperty('--va-scale', scale.toFixed(3));
      }
      window.addEventListener('resize', applyAccordionScale);
      applyAccordionScale();
      // Pin the .video-accordion section while scrolling through its .video-blocks
      // Helper to set a specific block active
      function setAccordionActive(index, progressOverride) {
        if (index < 0 || index >= blocks.length) return;
        blocks.forEach(b => {
          b.classList.remove('active');
          const v = b.querySelector('video');
          if (v && !v.paused) v.pause();
        });
        const active = blocks[index];
        active.classList.add('active');
        if (typeof progressOverride === 'number') {
          const pb = active.querySelector('.progress-bar .progress');
          if (pb) gsap.set(pb, { height: `${Math.max(0, Math.min(1, progressOverride)) * 100}%` });
        }
      }

      // --- MOBILE PIN STABILITY IMPROVEMENTS ---
      const MOBILE_BREAKPOINT = 768;
      const isMobileAccordion = window.innerWidth < MOBILE_BREAKPOINT;
      let lockedVH = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
      if (lockedVH < 420) lockedVH = 420; // floor to avoid extreme shrink during UI chrome transitions
      const getSegmentHeight = () => (isMobileAccordion ? lockedVH : window.innerHeight);

      let accordionPinST = ScrollTrigger.create({
        trigger: accordion,
        start: "top top",
        end: () => `+=${(blocks.length) * getSegmentHeight()}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: !isMobileAccordion,
        // markers: true,
        onRefresh: () => { if (window.__refreshAccordionState) window.__refreshAccordionState(); },
        onUpdate: () => { if (window.__refreshAccordionState) window.__refreshAccordionState(); },
        onEnter: () => { setAccordionActive(0, 0); },
        onEnterBack: () => { setAccordionActive(blocks.length - 1, 1); }
      });

      // Orientation change: allow one recalculation (safer moment for layout shift)
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          if (!accordionPinST) return;
            lockedVH = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
            if (lockedVH < 420) lockedVH = 420;
            accordionPinST.vars.end = `+=${(blocks.length) * getSegmentHeight()}`;
            accordionPinST.refresh();
            if (window.__refreshAccordionState) window.__refreshAccordionState();
        }, 350);
      });

      // Utility: scroll to a block's segment so onEnter/EnterBack handlers set active naturally
      function scrollToAccordionBlock(index) {
        const st = accordionPinST;
        if (!st) return;
        if (typeof st.start !== 'number') st.refresh();
        const segment = getSegmentHeight();
        const clampedIndex = Math.max(0, Math.min(index, blocks.length - 1));
        const insetRatio = 0.95;
        let target = st.start + clampedIndex * segment + (segment * insetRatio);
        if (clampedIndex === 0) target = st.start + (segment * insetRatio * 0.55);
        const maxTarget = (st.end - segment) + 4;
        if (target > maxTarget) target = maxTarget;
        if (target < st.start) target = st.start;
        if (window.lenis && typeof window.lenis.scrollTo === 'function') {
          window.lenis.scrollTo(target, { immediate: false });
        } else {
          window.scrollTo({ top: target, behavior: 'smooth' });
        }
      }
      // Expose for deep-linking/debug
      window.scrollToAccordionBlock = scrollToAccordionBlock;

      // Remove previous direct active toggling: instead scroll so progress makes it active organically
      blocks.forEach((videoAccordionBlock, idx) => {
        // Whole block click
        videoAccordionBlock.addEventListener('click', e => {
          if (e.target.closest('.video-player')) return; // ignore player (handled later)
          const alreadyActive = videoAccordionBlock.classList.contains('active');
            if (alreadyActive) return;
          scrollToAccordionBlock(idx);
        });
        // Title (h3) click explicitly
        const h3 = videoAccordionBlock.querySelector('h3');
        if (h3) {
          h3.style.cursor = 'pointer';
          h3.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            const alreadyActive = videoAccordionBlock.classList.contains('active');
            if (alreadyActive) return;
            scrollToAccordionBlock(idx);
          });
        }
      });

      // --- UNIFIED ACCORDION STATE & REVERSE-FILL PROGRESS ---
      function unifiedAccordionUpdate() {
        if (!accordionPinST) return;
        const startVal = typeof accordionPinST.start === 'number' ? accordionPinST.start : accordionPinST.start();
        const segment = getSegmentHeight();
        let offset = window.scrollY - startVal;
        if (offset < 0) offset = 0;
        const total = segment * blocks.length;
        if (offset > total) offset = total;
        const activeIndex = Math.min(blocks.length - 1, Math.max(0, Math.floor(offset / segment)));
        if (activeIndex !== window.__accordionActiveIndex) {
          blocks.forEach((b,i)=>{
            if (i === activeIndex) b.classList.add('active'); else b.classList.remove('active');
            const vid = b.querySelector('video');
            if (vid && i !== activeIndex && !vid.paused) vid.pause();
          });
          window.__accordionActiveIndex = activeIndex;
        }
        blocks.forEach((b,i)=>{
          const bar = b.querySelector('.progress-bar .progress');
          if (!bar) return;
          const blockStart = i * segment;
          const blockEnd = blockStart + segment;
          let p;
          if (offset >= blockEnd) p = 1;
          else if (offset <= blockStart) p = 0;
          else p = (offset - blockStart) / segment;
          gsap.set(bar, { height: `${(p*100).toFixed(3)}%` });
        });
      }
      window.__refreshAccordionState = unifiedAccordionUpdate;
      window.addEventListener('resize', () => {
        if (!accordionPinST) return;
        if (!isMobileAccordion) {
          accordionPinST.refresh();
        } else {
          if (window.visualViewport && Math.abs(window.visualViewport.height - lockedVH) > 140) {
            lockedVH = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
            if (lockedVH < 420) lockedVH = 420;
            accordionPinST.vars.end = `+=${(blocks.length) * getSegmentHeight()}`;
            accordionPinST.refresh();
          }
        }
        unifiedAccordionUpdate();
      });
      unifiedAccordionUpdate();
      ScrollTrigger.addEventListener('refresh', unifiedAccordionUpdate);
      ScrollTrigger.addEventListener('scrollEnd', unifiedAccordionUpdate);

      // Play video on .video-player click
      blocks.forEach(block => {
        const player = block.querySelector('.video-player');
        const video = player?.querySelector('video');
        const thumbnail = player?.querySelector('.thumbnail');

        if (player && video) {
          player.addEventListener('click', e => {
            e.stopPropagation();
            // Show modal
            videoModal.style.opacity = 1;
            videoModal.style.pointerEvents = 'auto';
            // Set modal video src and play
            const modalVideo = videoModal.querySelector('#modal-video');
            modalVideo.src = video.src;
            modalVideo.currentTime = 0;
            modalVideo.muted = false;
            modalVideo.play().catch(() => {});
          });
        }
      });
    }

    // Remove scroll-lock after all GSAP/ScrollTrigger pins are set
    document.body.classList.remove('scroll-lock');
  }

  // Enable nav scroll if .scrolling-video-cards does not exist
  if (!document.querySelector('.scrolling-video-cards')) {
    document.body.classList.add('nav-scroll-enabled');
  }

  function animateTitleLarge() {
    const titles = document.querySelectorAll('.title-large, .header-and-copy .title, .header-and-copy .title.mobile');
    titles.forEach(title => {
      // Skip animation if .title-large has a parent with .image-over-text
      let skip = false;
      let parent = title.parentElement;
      while (parent) {
        if (parent.classList && parent.classList.contains('image-over-text')) {
          skip = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (skip) return;
      // Only split if not already split
      if (!title.querySelector('span.char')) {
        // --- NEW: Split into lines, wrap each line in span.line, then wrap chars ---
        function splitLinesAndWrapChars(element, parentColor) {
          // Helper: recursively walk nodes, splitting on <br> and wrapping lines
          const lines = [];
          let currentLine = [];
          function walk(node, color) {
            if (node.nodeType === 3) { // Text node
              const text = node.textContent;
              for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const span = document.createElement('span');
                span.className = 'char';
                span.textContent = char === ' ' ? '\u00A0' : char;
                span.style.opacity = '0';
                span.style.color = '#ff0000';
                span.style.display = 'inline-block';
                span.style.transform = 'translateX(0)';
                if (color) span.dataset.finalColor = color;
                currentLine.push(span);
              }
            } else if (node.nodeType === 1) {
              if (node.tagName === 'BR') {
                // End current line, start new
                if (currentLine.length) lines.push(currentLine);
                currentLine = [];
              } else {
                // Prefer inline style color, then computed, then parent
                let nodeColor = color;
                if (node.style && node.style.color) {
                  nodeColor = node.style.color;
                } else {
                  try {
                    const computed = getComputedStyle(node);
                    if (computed.color) nodeColor = computed.color;
                  } catch (e) {}
                }
                // NEW: Pass down text-align as data attribute for later
                let nodeAlign = null;
                if (node.style && node.style.textAlign) {
                  nodeAlign = node.style.textAlign;
                } else {
                  try {
                    const computed = getComputedStyle(node);
                    if (computed.textAlign) nodeAlign = computed.textAlign;
                  } catch (e) {}
                }
                // For each child, pass down align info
                node.childNodes.forEach(child => {
                  if (child.nodeType === 3) {
                    // Text node: wrap and add align info
                    const text = child.textContent;
                    for (let i = 0; i < text.length; i++) {
                      const char = text[i];
                      const span = document.createElement('span');
                      span.className = 'char';
                      span.textContent = char === ' ' ? '\u00A0' : char;
                      span.style.opacity = '0';
                      span.style.color = '#ff0000';
                      span.style.display = 'inline-block';
                      span.style.transform = 'translateX(0)';
                      if (nodeColor) span.dataset.finalColor = nodeColor;
                      if (nodeAlign) span.dataset.originalAlign = nodeAlign;
                      currentLine.push(span);
                    }
                  } else {
                    walk(child, nodeColor);
                  }
                });
              }
            }
          }
          element.childNodes.forEach(child => walk(child, parentColor));
          if (currentLine.length) lines.push(currentLine);
          // Now wrap each line in span.line
          return lines.map(lineChars => {
            const lineSpan = document.createElement('span');
            lineSpan.className = 'line';
            // Inherit text-align from the original child if present
            let originalAlign = null;
            for (let c of lineChars) {
              if (c.dataset && c.dataset.originalAlign) {
                originalAlign = c.dataset.originalAlign;
                break;
              }
            }
            if (originalAlign) {
              lineSpan.style.textAlign = originalAlign;
              lineSpan.style.display = 'block';
              lineSpan.style.width = '100%';
              lineSpan.style.whiteSpace = 'nowrap';
            } else {
              lineSpan.style.display = 'block';
              lineSpan.style.width = '100%';
              lineSpan.style.whiteSpace = 'nowrap';
            }
            lineChars.forEach(char => lineSpan.appendChild(char));
            return lineSpan;
          });
        }
        // Use computed color of the title as the initial parent color
        const rootColor = getComputedStyle(title).color;
        // Save original HTML for reference
        const temp = document.createElement('div');
        temp.innerHTML = title.innerHTML;
        title.innerHTML = '';
        // Split into lines and wrap
        const lineSpans = splitLinesAndWrapChars(temp, rootColor);
        lineSpans.forEach(lineSpan => title.appendChild(lineSpan));
        // Restore original color
        title.style.color = rootColor;
      }
      // --- END NEW SPLIT ---
      // Animate by line: always top line first, then next line, etc.
      const lineSpans = Array.from(title.querySelectorAll('span.line'));
      // Determine if any line or the parent is right-aligned
      // Now: check each line for text-align right, else fallback to parent
      lineSpans.forEach((lineSpan, lineIndex) => {
        const chars = Array.from(lineSpan.querySelectorAll('span.char'));
        let isRightAligned = false;
        // Check line's text-align or any char's data-originalAlign
        if (getComputedStyle(lineSpan).textAlign === 'right') {
          isRightAligned = true;
        } else {
          for (let c of chars) {
            if (c.dataset.originalAlign === 'right') {
              isRightAligned = true;
              break;
            }
          }
        }
        // For right-aligned, animate left-to-right; else right-to-left
        const charsForStagger = isRightAligned ? chars : chars.slice().reverse();
        const xStart = isRightAligned ? -40 : 40;
        gsap.set(chars, {
          opacity: 0,
          color: '#ff0000',
          x: xStart,
          y: 0
        });
        ScrollTrigger.create({
          trigger: title,
          start: 'top 80%',
          onEnter: () => {
            gsap.to(charsForStagger, {
              opacity: 1,
              x: 0,
              y: 0,
              duration: 0.35,
              ease: 'power2.out',
              stagger: {
                each: 0.03,
                from: 'start'
              },
              delay: 0.05 + lineIndex * 0.08
            });
            // Color change with extra delay
            gsap.to(charsForStagger, {
              color: (i, el) => el.dataset.finalColor || getComputedStyle(title).color,
              duration: 0.25,
              ease: 'power2.out',
              stagger: {
                each: 0.03,
                from: 'start'
              },
              delay: 0.18 + lineIndex * 0.08 // extra color delay after fade/slide
            });
          },
          onLeaveBack: () => {
            gsap.set(chars, {
              opacity: 0,
              color: '#ff0000',
              x: xStart,
              y: 0
            });
          },
          once: false
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', animateTitleLarge);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      onFontsReady();
      animateTitleLarge();
      // Multi-pass refresh to stabilize pin calculations if loaded mid-scroll
      setTimeout(()=>{ ScrollTrigger.refresh(); }, 20);
      setTimeout(()=>{ ScrollTrigger.refresh(); }, 120);
      setTimeout(()=>{ ScrollTrigger.refresh(); }, 360);
    });
  } else {
    onFontsReady();
    animateTitleLarge();
    setTimeout(()=>{ ScrollTrigger.refresh(); }, 20);
    setTimeout(()=>{ ScrollTrigger.refresh(); }, 120);
    setTimeout(()=>{ ScrollTrigger.refresh(); }, 360);
  }

  // Parallax for .subtitle-title-image-more .fg (responsive & stronger downward motion)
  const stmBlocks = gsap.utils.toArray('.subtitle-title-image-more');
  if (stmBlocks.length) {
    const userScrollState = { scrolled: false };
    window.addEventListener('scroll', () => {
      if (!userScrollState.scrolled && Math.abs(window.scrollY) > 0) {
        userScrollState.scrolled = true;
      }
    }, { passive: true });

    stmBlocks.forEach(block => {
      const fg = block.querySelector('.fg');
      if (!fg) return;
      const getDistance = () => {
        const h = block.offsetHeight || fg.offsetHeight || window.innerHeight;
        const w = window.innerWidth;
        let mult;
        if (w < 600) mult = 0.6;
        else if (w < 900) mult = 0.21;
        else if (w < 1400) mult = 0.22;
        else mult = 0.28;
        return h * mult;
      };
      gsap.set(fg, { y: 0 });
      let distCache = getDistance();
      let baseProgress = null;
      const st = ScrollTrigger.create({
        trigger: block,
        start: 'top bottom', // engage as soon as block enters viewport from below (kept, but movement begins on first scroll)
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
        onRefresh: self => {
          distCache = getDistance();
          if (!userScrollState.scrolled) {
            baseProgress = null; // recapture after refresh
            gsap.set(fg, { y: 0 });
          }
        },
        onUpdate: self => {
          if (baseProgress === null) baseProgress = self.progress; // capture once, no early return
          let effective = self.progress - baseProgress;
          if (effective < 0) effective = 0;
          if (!userScrollState.scrolled && effective === 0) { // still before any user scroll
            gsap.set(fg, { y: 0 });
            return;
          }
          // Tiny easing at start to avoid sudden jump on slow first pixel scroll
          if (effective < 0.002) effective *= 0.35;
          gsap.set(fg, { y: distCache * effective });
        }
      });
      window.addEventListener('resize', () => { distCache = getDistance(); st.refresh(); });
    });
  }

  // Instagram in-app browser background paint workaround for .repeated-word
  (function instagramBGFix(){
    const ua = navigator.userAgent || navigator.vendor || '';
    // Detect Instagram in-app: usually 'Instagram' substring + WebKit
    if (/Instagram/i.test(ua)) {
      const rw = document.querySelector('.repeated-word');
      if (rw) rw.classList.add('is-inapp-bgfix');
    }
  })();
});